/**
 * Shared access — the delegation flow. Anyone whose position carries a G
 * (delegate) bit can hand a subset of their own permissions to another member
 * ("festival volunteer" case). Revoking cascades down the chain the grant
 * created. Server enforces invariant 1: you can only share bits you hold.
 * Wired: GET /api/positions/shared, GET /api/positions/registry,
 * GET /api/positions/share/candidates, POST /api/positions/share,
 * POST /api/positions/share/revoke.
 */
import React from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { space } from '@bero/tokens';
import { goBack, SmallButton } from '@/features/account-admin/bits';
import { Icon } from '@/features/account-admin/icons';
import { api } from '@/lib/client';
import { useTheme } from '@/theme';
import {
  AppText, Avatar, Button, Card, Chip, EmptyState, Input, Screen,
  SectionLabel, Sheet, Skeleton, TopBar,
} from '@/ui';

const BITS = ['read', 'write', 'edit', 'delete', 'approve', 'grant'] as const;
type Bit = (typeof BITS)[number];
const BIT_LABEL: Record<Bit, string> = {
  read: 'View', write: 'Create', edit: 'Edit', delete: 'Delete', approve: 'Approve', grant: 'Delegate',
};

type Grantable = { featureKey: string; bits: string[] };
type GivenGrant = Record<Bit, boolean> & { featureKey: string; userId: number; userName: string | null };
type MineGrant = Record<Bit, boolean> & { featureKey: string; grantedByName: string | null };
type Candidate = { id: number; fullName: string; role: string; flat: string | null };

export default function SharedAccessScreen() {
  const { colors } = useTheme();
  const [state, setState] = React.useState<'loading' | 'ready' | 'error'>('loading');
  const [grantable, setGrantable] = React.useState<Grantable[]>([]);
  const [given, setGiven] = React.useState<GivenGrant[]>([]);
  const [mine, setMine] = React.useState<MineGrant[]>([]);
  const [labels, setLabels] = React.useState<Record<string, string>>({});
  const [notice, setNotice] = React.useState<string | null>(null);

  // share sheet
  const [sharing, setSharing] = React.useState<Grantable | null>(null);
  const [candidates, setCandidates] = React.useState<Candidate[] | null>(null);
  const [query, setQuery] = React.useState('');
  const [person, setPerson] = React.useState<Candidate | null>(null);
  const [picked, setPicked] = React.useState<Set<Bit>>(new Set());
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setState('loading');
    try {
      const [sh, reg] = await Promise.all([
        api.get<{ grantable: Grantable[]; given: GivenGrant[]; mine: MineGrant[] }>('/api/positions/shared'),
        api.get<{ groups: { items: { key: string; label: string }[] }[] }>('/api/positions/registry'),
      ]);
      const map: Record<string, string> = {};
      for (const grp of reg.groups ?? []) for (const it of grp.items) map[it.key] = it.label;
      setLabels(map);
      setGrantable(sh.grantable ?? []);
      setGiven(sh.given ?? []);
      setMine(sh.mine ?? []);
      setState('ready');
    } catch {
      setState('error');
    }
  }, []);
  React.useEffect(() => { void Promise.resolve().then(() => load()); }, [load]);

  const openShare = (g: Grantable) => {
    setSharing(g);
    setPerson(null);
    setQuery('');
    setPicked(new Set(['read']));
    setNotice(null);
    if (candidates === null) {
      api.get<{ items: Candidate[] }>('/api/positions/share/candidates')
        .then((b) => setCandidates(b.items ?? []))
        .catch(() => setCandidates([]));
    }
  };

  const doShare = () => {
    if (!sharing || !person || picked.size === 0) return;
    setBusy(true);
    const bits = Object.fromEntries(BITS.map((b) => [b, picked.has(b)]));
    api.post<unknown>('/api/positions/share', { userId: person.id, featureKey: sharing.featureKey, ...bits })
      .then(() => { setSharing(null); setNotice(`Shared with ${person.fullName}.`); void load(); })
      .catch((e) => {
        setSharing(null);
        setNotice((e as { error?: string })?.error ?? 'Could not share access.');
      })
      .finally(() => setBusy(false));
  };

  const revoke = (row: GivenGrant) => {
    api.post<{ revokedCount: number }>('/api/positions/share/revoke',
      { userId: row.userId, featureKey: row.featureKey })
      .then((b) => {
        setNotice(b.revokedCount > 1
          ? `Revoked — including ${b.revokedCount - 1} further share${b.revokedCount > 2 ? 's' : ''} down the chain.`
          : 'Revoked.');
        void load();
      })
      .catch(() => setNotice('Could not revoke.'));
  };

  const bitsLine = (g: Record<Bit, boolean>) =>
    BITS.filter((b) => g[b]).map((b) => BIT_LABEL[b]).join(' · ');

  const filtered = (candidates ?? []).filter((c) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return c.fullName.toLowerCase().includes(q) || (c.flat ?? '').toLowerCase().includes(q);
  });

  return (
    <Screen scroll={false}>
      <TopBar title="Shared access" onBack={goBack} />
      <Screen padded style={{ paddingTop: space.s3 }}>
        {state === 'loading' ? (
          <View style={{ gap: space.s3 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={80} />)}</View>
        ) : state === 'error' ? (
          <EmptyState
            icon={<Icon name="alert-circle" size={40} color={colors.textTertiary} />}
            title="Couldn't load"
            body="Check your connection and try again."
            cta="Retry"
            onCta={() => { void load(); }}
          />
        ) : (
          <>
            {notice ? (
              <AppText variant="bodySm" weight="500" style={{ marginBottom: space.s3, color: colors.primary700 }}>
                {notice}
              </AppText>
            ) : null}

            <SectionLabel>You can share</SectionLabel>
            {grantable.length === 0 ? (
              <AppText variant="bodySm" tone="tertiary">
                Your positions don&apos;t carry a Delegate permission. Ask your committee
                if you need to hand access to someone.
              </AppText>
            ) : (
              <View style={{ gap: space.s3 }}>
                {grantable.map((g) => (
                  <Card key={g.featureKey}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
                      <View style={{ flex: 1, minWidth: 0 }}>
                        <AppText variant="bodyMd" weight="600">{labels[g.featureKey] ?? g.featureKey}</AppText>
                        <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                          You hold: {g.bits.map((b) => BIT_LABEL[b as Bit] ?? b).join(' · ')}
                        </AppText>
                      </View>
                      <SmallButton label="Share" icon="user-plus" onPress={() => openShare(g)} />
                    </View>
                  </Card>
                ))}
              </View>
            )}

            <SectionLabel style={{ marginTop: space.s5 }}>You shared</SectionLabel>
            {given.length === 0 ? (
              <AppText variant="bodySm" tone="tertiary">You haven&apos;t shared access with anyone.</AppText>
            ) : (
              <Card>
                {given.map((row, i) => (
                  <View key={`${row.userId}-${row.featureKey}`}
                    style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: space.s2, borderTopWidth: i > 0 ? 1 : 0, borderTopColor: colors.borderSoft }}>
                    <Avatar name={row.userName ?? '?'} size={36} />
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <AppText variant="bodyMd" weight="500">{row.userName ?? 'Member'}</AppText>
                      <AppText variant="bodySm" tone="tertiary">
                        {labels[row.featureKey] ?? row.featureKey} · {bitsLine(row)}
                      </AppText>
                    </View>
                    <SmallButton label="Revoke" danger onPress={() => revoke(row)} />
                  </View>
                ))}
              </Card>
            )}

            {mine.length > 0 ? (
              <>
                <SectionLabel style={{ marginTop: space.s5 }}>Shared with you</SectionLabel>
                <Card>
                  {mine.map((row, i) => (
                    <View key={row.featureKey}
                      style={{ paddingVertical: space.s2, borderTopWidth: i > 0 ? 1 : 0, borderTopColor: colors.borderSoft }}>
                      <AppText variant="bodyMd" weight="500">{labels[row.featureKey] ?? row.featureKey}</AppText>
                      <AppText variant="bodySm" tone="tertiary" style={{ marginTop: 2 }}>
                        {bitsLine(row)}{row.grantedByName ? ` · shared by ${row.grantedByName}` : ''}
                      </AppText>
                    </View>
                  ))}
                </Card>
              </>
            ) : null}
          </>
        )}
      </Screen>

      <Sheet visible={sharing !== null} onClose={() => setSharing(null)}>
        <SectionLabel>Share {sharing ? (labels[sharing.featureKey] ?? sharing.featureKey) : ''}</SectionLabel>
        {!person ? (
          <>
            <Input value={query} onChangeText={setQuery} placeholder="Search name or flat"
              autoFocus style={{ marginTop: space.s3 }} />
            <ScrollView style={{ maxHeight: 320, marginTop: space.s3 }} keyboardShouldPersistTaps="handled">
              {candidates === null ? (
                <View style={{ gap: space.s2 }}>{[0, 1, 2].map((i) => <Skeleton key={i} h={48} />)}</View>
              ) : filtered.length === 0 ? (
                <AppText variant="bodySm" tone="tertiary" style={{ paddingVertical: space.s3 }}>
                  No matching members.
                </AppText>
              ) : (
                filtered.slice(0, 40).map((c) => (
                  <Pressable key={c.id} accessibilityRole="button" onPress={() => setPerson(c)}
                    style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, paddingVertical: 10 }}>
                    <Avatar name={c.fullName} size={36} />
                    <View style={{ flex: 1, minWidth: 0 }}>
                      <AppText variant="bodyMd" weight="500">{c.fullName}</AppText>
                      <AppText variant="bodySm" tone="tertiary">
                        {[c.flat, c.role].filter(Boolean).join(' · ')}
                      </AppText>
                    </View>
                    <Icon name="chevron-right" size={16} color={colors.textTertiary} />
                  </Pressable>
                ))
              )}
            </ScrollView>
          </>
        ) : (
          <>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3, marginTop: space.s3 }}>
              <Avatar name={person.fullName} size={40} />
              <View style={{ flex: 1, minWidth: 0 }}>
                <AppText variant="bodyMd" weight="600">{person.fullName}</AppText>
                <AppText variant="bodySm" tone="tertiary">{person.flat ?? ''}</AppText>
              </View>
              <SmallButton label="Change" onPress={() => setPerson(null)} />
            </View>
            <AppText variant="bodySm" tone="tertiary" style={{ marginTop: space.s4, marginBottom: space.s2 }}>
              What can they do? (only permissions you hold yourself)
            </AppText>
            <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: space.s2 }}>
              {(sharing?.bits ?? []).map((b) => (
                <Chip key={b} label={BIT_LABEL[b as Bit] ?? b} active={picked.has(b as Bit)}
                  onPress={() => setPicked((s) => {
                    const next = new Set(s);
                    const bit = b as Bit;
                    if (next.has(bit)) next.delete(bit); else next.add(bit);
                    return next;
                  })} />
              ))}
            </View>
            <Button title={busy ? 'Sharing…' : 'Share access'} disabled={busy || picked.size === 0}
              style={{ marginTop: space.s4 }} onPress={doShare} />
          </>
        )}
      </Sheet>
    </Screen>
  );
}
